import React, { FC, useEffect, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  Container,
} from '@material-ui/core';
import Helmet from 'react-helmet';
import { useIsMobile } from '../../util/style-utils';
import { useTranslation } from '../../hooks/useTranslation';
import Categories from '../../components/blog/categories';
import { BlogArticleFragment } from '../../__generated__/gatsby-types';
import Carousel from '../../components/blog/carousel';
import colors from '../../themes/main-colors';
import RecentPosts from '../../components/blog/recent-posts';

export const blogHomePageQuery = graphql`
  {
    allArticles: allContentfulArticle(
      filter: {
        node_locale: { eq: "en" }
        slug: { ne: null, regex: "/^((?!placeholder).)*$/i" }
      }
      sort: { fields: [publishDate, updateDate], order: [DESC, DESC] }
    ) {
      totalCount
      nodes {
        ...BlogArticle
      }
    }

    featuredArticles: allContentfulBlogFeaturedArticles(
      filter: { contentful_id: { eq: "6byIzLVKIXjTmQnZ13zQKZ" } }
    ) {
      nodes {
        carouselArticles {
          ...BlogArticle
        }
        carouselAutoPlay
        carouselAutoPlayDelay
        spotlightArticle {
          ...BlogArticle
        }
      }
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: '0 auto',
      [theme.breakpoints.down('sm')]: {
        paddingRight: 0,
        paddingLeft: 0,
      },
    },
    navLine: {
      marginBottom: 0,
      color: `${colors.noirBlur}`,
      backgroundColor: `${colors.noirBlur}`,
    },
    recentPostBox: {
      [theme.breakpoints.up('sm')]: {
        padding: '0 67px 60px',
      },
    },
  }),
);

const Blog: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();

  /* UI hack: Reduce the perception of a 'flicker' on page load in desktop mode.
      Use minHeightShim to push the footer below the fold while waiting for the
      entries to show up on the page. Once the entries are displayed, remove the
      shim.
  */
  const [minHeightShim, setMinHeightShim] = useState(
    isMobile ? 'unset' : '80vh',
  );
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinHeightShim('unset');
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const { allArticles, featuredArticles } = useStaticQuery(blogHomePageQuery);

  const [carouselCards, setCarouselCards] = useState<BlogArticleFragment[]>([]);

  useEffect(() => {
    if (allArticles && allArticles?.nodes.length > 0) {
      const carouselArticleCards = featuredArticles?.nodes[0].carouselArticles;
      setCarouselCards(carouselArticleCards);
    }
  }, [allArticles]);

  const carousel = {
    panes: carouselCards?.map((card: any) => {
      return {
        header: card?.category?.name?.toLocaleUpperCase(),
        subHeader: card?.title,
        slug: card?.slug,
        articlePublishDate: card?.formattedPublishDate,
        image: card?.heroImage?.file?.url,
        imageFocusHorizontal: card?.imageFocusHorizontal,
        imageFocusVertical: card?.imageFocusVertical,
        backgroundColor: card?.backgroundColor,
      };
    }),
  };

  return (
    <>
      <Helmet bodyAttributes={{ class: 'TemplatePage' }}>
        <title>{t('BLOG_HOME_TITLE_SEO')}</title>
        <meta name="description" content={t('BLOG_HOME_DESCRIPTION_SEO')} />
      </Helmet>
      {!isMobile && <hr className={classes.navLine} />}
      <Container maxWidth="lg" style={{ paddingRight: 0, paddingLeft: 0 }}>
        <Categories />
      </Container>
      <Box>
        <div className={classes.root}>
          {carousel?.panes?.length ? (
            <Box>
              <Carousel
                panes={carousel?.panes}
                carouselAutoPlay={featuredArticles?.nodes[0]?.carouselAutoPlay}
                carouselAutoPlayDelay={
                  featuredArticles?.nodes[0]?.carouselAutoPlayDelay
                }
              />
            </Box>
          ) : null}
          <Container
            maxWidth="lg"
            className={classes.recentPostBox}
            style={{ minHeight: minHeightShim }}
          >
            <RecentPosts
              title={t('MOST_RECENT_POSTS')}
              articleData={allArticles?.nodes}
              featured={featuredArticles?.nodes[0]}
            />
          </Container>
        </div>
      </Box>
    </>
  );
};

export default Blog;
